import { SharedState } from '@trello/shared-state';

/**
 * Discriminated union representing a single data source's loading lifecycle.
 *
 * - `error`: The most recent fetch failed.
 * - `initializing`: First load, no data available yet.
 * - `navigating`: User navigated to a new date range. `isCached` indicates
 *   whether the range was already in the Apollo cache.
 * - `ready`: Data is available. `isStale` is true when data exists but may
 *   be outdated (e.g. after a reconnection before the refetch completes).
 * - `refetching`: A background refetch is in progress; stale data is still
 *   shown.
 */
export type PlannerLoadingPhase =
  | { status: 'error'; error: Error }
  | { status: 'initializing' }
  | { status: 'navigating'; isCached: boolean }
  | { status: 'ready'; isStale: boolean }
  | { status: 'refetching' };

/**
 * The string status values that a PlannerLoadingPhase can have.
 */
export type PlannerLoadingStatus = PlannerLoadingPhase['status'];

/**
 * Composite loading state for every loadable data source in Planner.
 *
 * Each key represents one independent source. New sources are added by
 * extending this interface and providing an initial phase below.
 *
 * ── Adding a new source ───────────────────────────────────────────────
 *
 * Add a new source here only if it has a stable, user-meaningful loading
 * lifecycle. This store is a semantic orchestration layer, not a mirror of
 * whatever transient booleans a hook happens to expose.
 *
 * Before adding a source, answer these questions:
 * 1. What does `initializing` mean for this source?
 * 2. What does `navigating` mean? If it navigates, can it determine `isCached`?
 * 3. What does `refetching` mean? Does stale/renderable data remain visible?
 * 4. When is `ready` reached?
 * 5. Under what explicit reset boundary may it return to `initializing`?
 *
 * Producer rules:
 * - Publish phases only at user-meaningful lifecycle boundaries.
 * - Do NOT publish from transient implementation details like cache churn,
 *   fragment completeness flicker, or pagination booleans with no UX impact.
 * - Once a source reaches `ready`, do not regress to `initializing` unless a
 *   true lifecycle reset occurred.
 *
 * Consumer rules:
 * - Being tracked here does NOT automatically mean the source should gate the
 *   full Planner loading screen.
 * - Consumers should intentionally choose the subset of sources they care
 *   about, e.g. `usePlannerLoadingPhase(['events', 'upsellData'])`.
 */
export interface PlannerLoadingStateValue {
  events: PlannerLoadingPhase;
  dueDates: PlannerLoadingPhase;
  upsellData: PlannerLoadingPhase;
  accountData: PlannerLoadingPhase;
  /**
   * Schedule View's own events fetch. Peer to `events` (FullCalendar's
   * range-driven query) — different lifecycle: SV's range is fixed and
   * resets on view-switch unmount.
   */
  scheduleViewEvents: PlannerLoadingPhase;
}

export type PlannerLoadingSource = keyof PlannerLoadingStateValue;

/**
 * Initial state for every registered source.
 *
 * When you add a new key to `PlannerLoadingStateValue`, add the corresponding
 * initial phase here in the same change.
 */
const INITIAL_STATE: PlannerLoadingStateValue = {
  events: { status: 'initializing' },
  dueDates: { status: 'initializing' },
  upsellData: { status: 'initializing' },
  accountData: { status: 'initializing' },
  scheduleViewEvents: { status: 'initializing' },
};

// ── Shared helpers (used by both the class and React hooks) ───────────

/**
 * Returns `true` when the given phase represents a loading-like state
 * (`initializing`, `navigating` with no cache, or `refetching`).
 */
export function isLoadingPhase(phase: PlannerLoadingPhase): boolean {
  switch (phase.status) {
    case 'initializing':
    case 'refetching':
      return true;
    case 'navigating':
      return !phase.isCached;
    default:
      return false;
  }
}

/**
 * Returns the phases for the given sources (or all sources if none specified).
 */
export function getSourcePhases(
  state: PlannerLoadingStateValue,
  sources?: PlannerLoadingSource[],
): PlannerLoadingPhase[] {
  const keys = sources ?? (Object.keys(state) as PlannerLoadingSource[]);
  return keys.map((key) => state[key]);
}

/**
 * SharedState subclass that aggregates loading phases from multiple
 * independent data-loading hooks.
 *
 * Producers call {@link setSourcePhase} to publish their current phase.
 * Consumers read per-source or aggregate status through helper methods
 * (or the companion React hooks).
 */
class PlannerLoadingState extends SharedState<PlannerLoadingStateValue> {
  constructor() {
    super(INITIAL_STATE);
  }

  // ── Producer API ─────────────────────────────────────────────────────

  /**
   * Publish a new loading phase for a single data source.
   */
  setSourcePhase(
    source: PlannerLoadingSource,
    phase: PlannerLoadingPhase,
  ): void {
    this.setValue((prev) => ({ ...prev, [source]: phase }));
  }

  // ── Per-source helpers ───────────────────────────────────────────────

  /**
   * Returns `true` when the given source is in a loading-like state
   * (`initializing`, `navigating` with no cache, or `refetching`).
   */
  isPhaseLoading(source: PlannerLoadingSource): boolean {
    return isLoadingPhase(this.value[source]);
  }

  /**
   * Returns the `Error` for the given source if it is in the `error`
   * status, otherwise `undefined`.
   */
  getPhaseError(source: PlannerLoadingSource): Error | undefined {
    const phase = this.value[source];
    return phase.status === 'error' ? phase.error : undefined;
  }

  // ── Aggregate helpers ────────────────────────────────────────────────

  /**
   * `true` when at least one source (or one of the specified sources) is
   * in a loading state.
   */
  isAnyLoading(sources?: PlannerLoadingSource[]): boolean {
    return getSourcePhases(this.value, sources).some(isLoadingPhase);
  }

  /**
   * `true` when every source (or every specified source) has reached a
   * non-loading state (`ready` or `error`).
   */
  isFullyLoaded(sources?: PlannerLoadingSource[]): boolean {
    return getSourcePhases(this.value, sources).every(
      (phase) => !isLoadingPhase(phase),
    );
  }

  /**
   * `true` when at least one source (or one of the specified sources) is
   * in the `error` status.
   */
  isAnyError(sources?: PlannerLoadingSource[]): boolean {
    return getSourcePhases(this.value, sources).some(
      (phase) => phase.status === 'error',
    );
  }
}

// oxlint-disable-next-line @trello/no-module-logic
export const plannerLoadingState = new PlannerLoadingState();
